/**
 * StandListPage.jsx — Proje Stand Listesi
 * Route: /Management/Projects   (eski /Management/Projects/Stands buraya yönleniyor)
 *
 * 2026-09-15: menüdeki "Projeler" tek bağlantısı bu sayfa. Proje Özel İzinleri
 * tepsisi (/Management/Projects/SpecialPermits) menüden çıktı, araç çubuğundaki
 * "Özel İzinler" düğmesinden açılıyor — rozet bekleyen talep sayısı, çünkü her
 * bekleyen talep bir projeyi kilitliyor.
 *
 * Fuardaki tüm standları (proje kaydı olan/olmayan) listeler. Görsel dil,
 * "fromImageToLayout" ile verilen referans ekran görüntüsünden çıkarıldı:
 * Liste/Kart görünüm anahtarı, standNo/standCode rozetli kare küçük resim,
 * Yerli-Yabancı + Paket/Pavilyon/Özel bilgi rozetleri (dekoratör atanmamışsa
 * ya da borç/inşa izni/iş izni "kötü" ise satır/kart kenarlığı kırmızıya
 * döner — bkz. hasIssue). Bu alanlar standOverviewService.js'te
 * _standListDesignOverrides haritasıyla mevcut mock verilere eklendi — veri
 * kaynağı ve mevcut sayfanın temel akışı (arama, sayfalama, Görüntüle→detay)
 * değişmedi, sadece görünüm bu referansa göre yeniden tasarlandı.
 *
 * 2026-07-10 revizyonu (bkz. `/revise-page`): kare rozetin sağındaki sütun artık
 * hall/stand adını tekrarlamıyor — firmaShortName (standın kısa görünen adı,
 * örn. "Acme Mobilya" — tüzel unvan firmaName'den ayrı bir alan) ve altında
 * dekoratör/yüklenici adını gösteriyor. Küçük rozet satırı Borç/İnşa İzni/İş
 * İzni'ni bırakıp Yerli-Yabancı + stand tipi + proje tipi (moduler/ahşap/diğer)
 * + proje yapısı (moduler→maxima/octonorm, ahşap→mdf/sunta/ağaç) gösterecek
 * şekilde değişti — hasIssue kenarlık mantığı görünmeyen bir sürücü olarak
 * aynen kalıyor. Rozetlerle Detaylar butonu arasına, standın evrak akışını
 * özetleyen FileStatusIndicator (yeni/kabul/red dosya sayıları) eklendi.
 *
 * 2026-07-10 revizyonu, devam: statusId/DEAL_STATUS/FINANCE_APPROVAL üçlü rozet
 * satırı kart üzerinden kaldırıldı. statusId (proje durumu) kasıtlı olarak hiçbir
 * yerde render edilmiyor artık — ileride kart/kutu etrafında bir renkle ifade
 * edilecek, o karar netleşene kadar UI'da yok. Anlaşma durumu, finans onayı ve
 * daha önce pillerden çıkarılan borç/inşa izni/iş izni bilgileri denetçiyi
 * kalabalıkla boğmamak için tek bir StandExtraInfoIndicator ikonunda toplandı —
 * üzerine gelince (hover) Popover ile açılır; ikonun rengi hasIssue ile aynı
 * sinyali taşır.
 */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Button, Input,
  InputGroup, Badge, Spinner, Alert, Collapse,
  UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
  UncontrolledPopover, PopoverBody,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import {
  Search, RefreshCw, Eye, Pencil,
  Handshake, Snowflake, ChevronLeft, ChevronRight,
  // WAS (until #214; the commented-out StandThumbnail below needs Wrench back): LayoutGrid, List as ListIcon, Filter, ArrowUpDown, Wrench,
  LayoutGrid, List as ListIcon, Filter, ArrowUpDown,
  Coins, MoreVertical, UserPlus, Plus,
  HardHat, AlertTriangle, Inbox, ShieldCheck,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DurumSayaclari from "../../../Components/Common/DurumSayaclari";
import MainContext from "../../../Components/Contexts/MainContext";
import {
  getStandOverviewList, updateStand, assignFirmaToStand, getSalonOptions,
  COMMITMENT_STATE,
} from "../../../services/standOverviewService";
import { standCodeFromName, isStandAdiCakismasi } from "../../../services/standNaming";
// Salon + stand kodu + tabela adı — Stand Yarat sayfasıyla PAYLAŞILAN künye bloğu.
// "Hol No" serbest metin kutusu 2026-09-04'te kalktı: adın hol yarısını sunucu
// salonun kendi adından kuruyor (bkz. Components/Projects/StandIdentityFields.jsx).
import StandIdentityFields from "../../../Components/Projects/StandIdentityFields";
// Bilgi pilleri, izin rozetleri ve hasIssue kararı Stand Detayı ile PAYLAŞILIYOR —
// bkz. Components/Projects/StandPills.jsx (iki sayfanın aynı standa farklı izin durumu
// yazmasına yol açan 2026-08-29 kaymasının kapatılması).
import {
  VDivider, StandBadgeRow, StandBadgeRowFull, hasIssue, StandExtraInfoIndicator,
  getDocStatusGroups, StatusTile, COMMITMENT_ICONS,
  permitBadge, statusBackground, NEUTRAL_GRAY,
} from "../../../Components/Projects/StandPills";
// #214: the stand-code box of the company panel (Standlarim / Dekorator Projeler), shared.
import StandKodKutusu from "../../../Components/Firma/StandKodKutusu";
// #506: the Elektrik square carries the signature level (decorator / decorator + electrician) as a dot and in its popover.
import ImzaSeviyesiRozeti from "../../../Components/Projects/ImzaSeviyesiRozeti";
import { seviyeNoktasi, seviyeMetni } from "../../../Components/Projects/taahhutnameYardimci";
// #548: the stand's own name (tabelaAdi) leads a row, the company steps down to the small line.
import { standBaslik } from "../../../Components/Projects/standBaslik";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import CreateFirmaModal from "../../../Components/Firma/CreateFirmaModal";
import FirmaAtamaTalepPanel from "../../../Components/Firma/FirmaAtamaTalepPanel";
import { FIRMA_TYPE } from "../../../services/firmaAramaService";
import {
  FILE_APPROVAL_STATUS, getOpenRevisionStandIds, getOpenRevisionCounts,
  getPendingProposalCounts,
  getProjectByStandId,
  getBekleyenOzelIzinler,
} from "../../../services/projectService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// Canlı backend'e sahip test fuarları (298/299) — bkz. FirmaProjelerListPage.jsx/
// FirmaStandListPage.jsx'teki aynı desen (dosya-yerel kopya).
// WAS (until #398): only the two seeded test fairs ran on the real backend, every other fair fell back to mock data:
// const LIVE_TEST_FUAR_IDS = [298, 299];
// const isLiveTestFuarId = (fuarId) => LIVE_TEST_FUAR_IDS.includes(Number(fuarId));
// #398: a full-simulation test creates a NEW fair, so every real fair id is live.
const isLiveTestFuarId = (fuarId) => Number(fuarId) > 0;

// 2026-08-29: taahhütnameleri uploadedFiles'tan TÜRETEN yerel yardımcı kaldırıldı —
// stand_file 5'in alt dosyalarını tarayıp onay durumu uyduruyordu. Roster artık
// commitments[]'i doğru şekilde (kaynak belge + üretim zinciri durumu) döndürüyor,
// bkz. missing-pills-guide.md §5.

// Canlı standId köprüsü. 2026-08-29'dan beri liste render'ı için GEREKLİ DEĞİL —
// piller, izinler ve taahhütnameler roster'ın kendisinden gelir. Köprüden geriye
// SADECE belge kutucuklarının okuduğu fileApprovals/uploadedFiles ve proje kabuğunun
// varlığı kaldı, bu yüzden ikinci ve ENGELLEMEYEN bir aşamada çekiliyor.
// `hasProjectShell` YALNIZCA canlı köprüde set edilir (`bridged !== null`) — mock
// kayıtlarda bu alan hiç yok, `!== false` kontrolü mock'ta true'ya düşer.
function mergeLiveProjectIntoRow(row, bridged) {
  const requiredCount = bridged?.requiredCount ?? 0;
  const approvedCount = bridged?.approvedCount ?? 0;
  return {
    ...row,
    hasProjectShell: bridged !== null,
    // ⚠️ 2026-08-29: standTypeName/projectHeightNumber/constructionPermitGranted/
    // commitments ARTIK BURADA EZİLMİYOR — roster'ın kendisi taşıyor (bkz.
    // missing-pills-guide.md). Köprünün cevabı gecikirse ya da 404 dönerse
    // (proje kaydı olmayan stand) pillerin kaybolmaması için de böyle olmalı.
    // Köprüden GERÇEKTEN gereken tek şey belge kutucukları kaldı.
    requiredCount,
    approvedCount,
    fileApprovals: bridged?.fileApprovals ?? [],
    uploadedFiles: bridged?.uploadedFiles ?? [],
  };
}

// Durum kutusu: Proje Evrakları / Proje Durumu sekmelerindeki bir kategorinin genel
// onay durumunu (Beklemede/Onaylandı/Reddedildi — FILE_APPROVAL_STATUS) tek bir renkli
// karede özetler. Tıklanınca (StandExtraInfoIndicator'daki popover ile aynı desen)
// kategori adı, durum rozeti ve varsa bağlı dosya adları açılır.
// `badge` verilirse FILE_APPROVAL_STATUS yerine o kullanılır — taahhütname kutuları
// artık onay durumu değil ÜRETİM ZİNCİRİ durumu taşıyor (COMMITMENT_STATE), ve o
// zincirin 6 durumu 0/1/2 onay şemasına sığmıyor.
// #506: `level` / `elektrikciName` (Elektrik only, from commitments[]) add a dot on the square and the level badge to the popover.
function StatusSquare({ domId, icon: Icon, label, status, fileNames, badge, level, elektrikciName }) {
  const { t } = useTranslation("projeler");
  const info = badge ?? FILE_APPROVAL_STATUS[status] ?? FILE_APPROVAL_STATUS[0];
  const seviyeMetniAdi = seviyeMetni(level);
  return (
    <>
      {/* 2026-09-04: 30×30 + `var(--vz-*)` idi. Referans /Firma/Projeler'in StatusTile'ı
          (40×40, 18px ikon, sabit hex palet) — `--vz-warning` rgb(255,127,65) olduğu için
          aynı "Beklemede" durumu firma tarafında sarı-turuncu, burada kırmızı-turuncu
          görünüyordu. Ölçü ve palet artık StandPills'ten. */}
      <StatusTile
        id={domId}
        icon={Icon}
        color={info.color}
        label={seviyeMetniAdi ? `${label} — ${seviyeMetniAdi}` : label}
        marker={seviyeNoktasi(level) ?? undefined}
        interactive
      />
      <UncontrolledPopover target={domId} trigger="legacy" placement="top">
        <PopoverBody style={{ minWidth: 180 }}>
          <div className="fw-semibold mb-1">{label}</div>
          <Badge color={info.color}>{info.label}</Badge>
          <ImzaSeviyesiRozeti level={level} elektrikciName={elektrikciName} className="mt-2" />
          {fileNames && fileNames.length > 0 ? (
            <div className="small text-muted mt-2">
              {fileNames.map((n) => <div key={n} className="text-truncate">{n}</div>)}
            </div>
          ) : (
            <div className="small text-muted fst-italic mt-2">{t("standList.belgeSecilmedi")}</div>
          )}
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
}

// İlk 3 kutu: Proje Evrakları sekmesindeki belge gruplarının (bkz. DOC_GROUPS,
// StandOverviewDetailPage.jsx) standFileId bazlı genel durumu.
// 2026-09-04: bu üçlü artık burada SABİT DEĞİL — canlı ve mock stand_file id'leri
// farklı ve buradaki eski liste MOCK id'leriydi, yani canlı fuarlarda kutular yanlış
// başlığın durumunu gösteriyordu (bkz. StandPills.getDocStatusGroups).

// Taahhütname ikonları (COMMITMENT_ICONS) artık StandPills'ten — firma tarafındaki
// COMMITMENT_GROUPS ile aynı eşleme.

// Liste kartındaki 3+3 durum kutusu satırı — StandOverviewDetailPage.jsx'teki aynı
// fileApprovals/commitments alanlarını okur, ayrı bir mock veri gerektirmez.
function StandStatusBoxes({ s, isLive }) {
  const docStatusGroups = getDocStatusGroups(isLive);
  const fileApprovalsMap = {};
  (s.fileApprovals || []).forEach((fa) => { fileApprovalsMap[fa.standFileId] = fa.status; });
  const uploadedFiles = s.uploadedFiles || [];
  const commitments = s.commitments || [];

  return (
    <div className="d-flex align-items-stretch flex-wrap gap-3">
      <div className="d-flex align-items-center gap-2">
        {docStatusGroups.map((g) => (
          <StatusSquare
            key={g.standFileId}
            domId={`doc-status-${s.id}-${g.standFileId}`}
            icon={g.icon}
            label={g.label}
            status={fileApprovalsMap[g.standFileId] ?? 0}
            fileNames={uploadedFiles.filter((f) => f.standFileId === g.standFileId).map((f) => f.originalFileName)}
          />
        ))}
      </div>
      {commitments.length > 0 && (
        <>
          <VDivider className="d-none d-sm-block" />
          <div className="d-flex align-items-center gap-2">
            {/* Canlı şekil {key,label,sourceStandFileId,sourceSubFileId,sourceName,state}
                ile mock şekli {key,label,status,fileId} bir arada desteklenir. Taahhütname
                YÜKLENMİYOR: kaynak belge (İSG←3D Köşe, Elektrik←Kuşbakışı, Statik←Statik
                Hesap) yükleniyor, inceleyen kişi seçiyor, sistem üretiyor — bu yüzden
                kutunun anlattığı şey "onaylandı mı" değil "zincir nereye kadar geldi". */}
            {commitments.map((c) => (
              <StatusSquare
                key={c.key}
                domId={`commitment-status-${s.id}-${c.key}`}
                icon={COMMITMENT_ICONS[c.key] ?? HardHat}
                label={c.label}
                status={c.status}
                badge={c.state ? COMMITMENT_STATE[c.state] : undefined}
                level={c.level}
                elektrikciName={c.elektrikciName}
                fileNames={
                  c.sourceName
                    ? [c.sourceName]
                    : c.fileId
                      ? [uploadedFiles.find((f) => f.id === c.fileId)?.originalFileName].filter(Boolean)
                      : []
                }
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Dosya durumu bildirimi: küçük kutular ile Detay butonu arasında, standın
// evrak akışını özetler — incelenecek yeni (status 0), kabul edilen (status 1)
// ve reddedilen (status 2) dosya sayıları + standId köprüsüyle bitmemiş revizyon
// sayısı (bkz. projectService.js bölüm 11, getOpenRevisionCounts). #283: renkli
// kareler (DurumSayaclari), sıfırlar çizilmez, hepsi stand detayına YENİ SEKMEDE
// açılır — ⏱ ✓ ✗ Proje Evrakları sekmesine, ↺ Revizyonlar sekmesine (?tab=, durum
// süzgeci yok).
// ⚠️ "Revizyon istenen" sayacı RESOLVED olmayan HER revizyonu sayar (ANSWERED
// dahil) — dekoratör yanıtladı diye stand üzerinde çalışılmıyor olmuyor.
function FileStatusIndicator({ s, openRevisionCount }) {
  const { t } = useTranslation("projeler");
  const approvals = s.fileApprovals || [];
  const detayUrl = `/Management/Projects/Stands/${s.id}`;
  return (
    <DurumSayaclari
      items={[
        { key: "pending", tone: "warning", value: approvals.filter((f) => f.status === 0).length, label: t("standList.incelenecekYeniDosya"), href: `${detayUrl}?tab=files` },
        { key: "revision", tone: "info", value: openRevisionCount ?? 0, label: t("standList.revizyonIstenen"), href: `${detayUrl}?tab=revisions` },
        { key: "approved", tone: "success", value: approvals.filter((f) => f.status === 1).length, label: t("standList.kabulEdilenDosya"), href: `${detayUrl}?tab=files` },
        { key: "rejected", tone: "danger", value: approvals.filter((f) => f.status === 2).length, label: t("standList.reddedilenDosya"), href: `${detayUrl}?tab=files` },
      ]}
    />
  );
}

// Bitmemiş revizyon rozeti: standId, projectService.js'teki bir revizyon konuşmasına
// standId köprüsüyle bağlıysa (bkz. getOpenRevisionStandIds) gösterilir — detaylar
// StandOverviewDetailPage.jsx'in Revizyonlar sekmesinde.
function OpenRevisionBadge() {
  const { t } = useTranslation("projeler");
  return (
    <Badge color="danger" className="ms-1" title={t("standList.kapanmamisRevizyonVar")}>
      <AlertTriangle size={11} className="me-1" />‼
    </Badge>
  );
}

// "Sıra bizde" rozeti — ANSWERED revizyonu olan standlar (getPendingProposalCounts).
// ⚠️ ‼ rozetinden AYRI bir bilgi ve kasıtlı olarak ayrı duruyor: ‼ "bu standda bir
// sorun var" der, bu "dekoratör yanıtını gönderdi, KARAR BENDE" der. İkisi aynı anda
// doğru olabilir; tek rozete katlamak bir değişiklik talebini dekoratör aleyhine bir
// bulgu gibi gösterirdi. Asıl iş kuyruğu budur, ‼ bir durum rozetidir.
function PendingProposalBadge({ count }) {
  const { t } = useTranslation("projeler");
  return (
    <Badge color="warning" className="ms-1" title={t("standList.dekoratorunOnerisiKararBekliyor")}>
      <Inbox size={11} className="me-1" />{count}
    </Badge>
  );
}

// Kare küçük resim: sol üstte dekoratif anahtar rozeti, ortada standNo (büyük) /
// standCode (küçük) iki satır. Referans görseldeki köşeli parantez çerçevesi yerine
// tema tokenlarıyla (bg-dark + border) sade bir kare kullanıldı.
// WAS (until #214): a plain dark box, not clickable, the same colour for every stand.
// function StandThumbnail({ standNo, standCode }) {
//   const { t } = useTranslation("projeler");
//   return (
//     <div
//       className="position-relative bg-dark text-white rounded d-flex flex-column align-items-center justify-content-center flex-shrink-0"
//       style={{ width: 64, height: 64 }}
//     >
//       <span
//         className="position-absolute top-0 start-0 translate-middle bg-success text-white rounded-circle d-flex align-items-center justify-content-center"
//         style={{ width: 22, height: 22 }}
//         title={t("standList.dekorasyonTipi")}
//       >
//         <Wrench size={12} />
//       </span>
//       <div className="fw-bold fs-5 lh-1">{standNo}</div>
//       <div className="small lh-1">{standCode}</div>
//     </div>
//   );
// }

// #214: the stand-code box is the company panel's (Components/Firma/StandKodKutusu), coloured by the stand's
// Insa Izni status (the colour comes from the database: permitBadge) and a click opens the stand's project detail.
// A stand without a participant has no project to judge, so its box stays neutral gray (hasIssue skips it too).
export function StandThumbnail({ s, onOpen }) {
  const { t } = useTranslation("projeler");
  const permit = s.firmaId ? permitBadge(s) : null;
  return (
    <StandKodKutusu
      stand={s}
      color={permit ? statusBackground(permit.color) : NEUTRAL_GRAY}
      onClick={onOpen}
      hint={permit ? t("standOverviewDetail.insaIzni", { label: permit.label }) : t("standList.detaylar")}
      badgeTitle={t("standList.dekorasyonTipi")}
    />
  );
}

function StandActionsDropdown({ onView, onEdit, onInfo }) {
  const { t } = useTranslation("projeler");
  return (
    <UncontrolledDropdown>
      <DropdownToggle color="info" outline size="sm" caret>
        <MoreVertical size={14} className="me-1" />{t("standList.islemler")}
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={onView}>
          <Eye size={14} className="me-2" />{t("common:goruntule")}
        </DropdownItem>
        <DropdownItem onClick={onEdit}>
          <Pencil size={14} className="me-2" />{t("common:duzenle")}
        </DropdownItem>
        <DropdownItem divider />
        {/* Dekoratör ataması artık Düzenle popup'ında (doğrudan atama + eşleştirme
            talepleri aynı ekranda dursun diye) — bu satır oraya kısayol. */}
        <DropdownItem onClick={onEdit}>
          <UserPlus size={14} className="me-2" />{t("standList.dekoratorAta")}
        </DropdownItem>
        <DropdownItem onClick={() => onInfo(t("standList.borcIzinDurumuGuncellemeOzelligi"))}>
          <Coins size={14} className="me-2" />{t("standList.borcIzinDurumunuGuncelle")}
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

// ─── MODAL: Stand Düzenle ─────────────────────────────────────────────────────
// Üç ayrı iş, üç ayrı uç — bilerek tek bir "Kaydet" altında toplanmadılar:
//   1) Stand künyesi (ad/salon/tabela adı) — PUT /standTalep/update, tek Kaydet.
//   2) Doğrudan firma ataması — POST /assignFirma. Kendi butonu var çünkü her
//      basış standa BİR SATIR yazar; bir formun yan etkisi olarak çalışmamalı.
//   3) Firma eşleştirme talepleri — stand tarafının serbest metinle açtığı istekler;
//      denetmen gerçek firmayı seçince onaylanır (FirmaAtamaTalepPanel). Talep
//      akışının BÜTÜN sebebi budur: seviye (relation_level) sunucuda türetilir,
//      firmayı seçen insan olur (bkz. FE-stand-firma-atama-talebi-guide.md).
function EditStandModal({ isOpen, onClose, onSaved, stand, fuarId, forceLive }) {
  const { t } = useTranslation("projeler");
  const [form, setForm] = useState({
    // Roster satırı `standCode`'u zaten taşıyor (mapRosterRow); yoksa tam addan
    // türetiliyor — ikisi de aynı parse (bkz. services/standNaming.js).
    standCode: stand?.standCode || standCodeFromName(stand?.standName),
    salonId: stand?.salonId != null ? String(stand.salonId) : "",
    tabelaAdi: stand?.tabelaAdi || "",
    tabelaAdiTouched: true, // mevcut değere saygı — düzenlemede öneri yok
  });
  const [salonOptions, setSalonOptions] = useState([]);
  const [salonsLoaded, setSalonsLoaded] = useState(false);
  const [katilimciFirma, setKatilimciFirma] = useState(null);
  const [dekoratorFirma, setDekoratorFirma] = useState(null);
  const [createFirmaFor, setCreateFirmaFor] = useState(null);
  const [assigning, setAssigning] = useState(null); // "katilimci" | "dekorator" | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  // #547: sunucu "bu holde bu adla stand var" dedi → Stand Kodu kutusu kırmızı, kod ya da hol değişince söner
  const [kodCakisti, setKodCakisti] = useState(false);

  useEffect(() => {
    if (!isOpen || !fuarId) return undefined;
    let ignore = false;
    getSalonOptions(fuarId, { forceLive })
      .then((list) => { if (!ignore) { setSalonOptions(list || []); setSalonsLoaded(true); } })
      .catch(() => { if (!ignore) { setSalonOptions([]); setSalonsLoaded(true); } });
    return () => { ignore = true; };
  }, [isOpen, fuarId, forceLive]);

  function update(patch) {
    if ("standCode" in patch || "salonId" in patch) setKodCakisti(false);
    setForm((f) => ({ ...f, ...patch }));
  }

  // Standın kendi salonu fuarın salon listesinde OLMAYABİLİR — yerel veride
  // gerçekten oluyor (fuar 298'in fuarSalons'u yalnız salon 94 derken standları
  // salon 1'de duruyor; krokiService.getSalonlarByFuar aynı durumu `isUnlisted`
  // ile ele alıyor). Eksikse listeye başa ekleniyor, yoksa <select> eşleşen option
  // bulamayıp sessizce "Seçiniz…" gösterir ve salon yanlış görünür.
  const salonOptionsWithCurrent = React.useMemo(() => {
    const currentId = stand?.salonId;
    if (currentId == null) return salonOptions;
    if (salonOptions.some((s) => String(s.salonId) === String(currentId))) return salonOptions;
    return [{ salonId: currentId, salonName: stand?.salonName || `Salon ${currentId}` }, ...salonOptions];
  }, [salonOptions, stand?.salonId, stand?.salonName]);

  const salonName = salonOptionsWithCurrent
    .find((s) => String(s.salonId) === String(form.salonId))?.salonName || "";
  const kod = form.standCode.trim();
  const mevcutKod = stand?.standCode || standCodeFromName(stand?.standName);
  const codeChanged = kod !== "" && kod !== mevcutKod;
  const salonChanged = form.salonId !== "" && String(form.salonId) !== String(stand?.salonId ?? "");
  const tabelaChanged = form.tabelaAdi.trim() !== (stand?.tabelaAdi || "");
  const dirty = codeChanged || salonChanged || tabelaChanged;

  async function handleSave() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    setOkMsg(null);
    setKodCakisti(false);
    try {
      // Kısmi güncelleme: yalnızca DEĞİŞEN anahtarlar gidiyor. tabelaAdi'nin boş
      // hâli "sil" emri olduğu için bilerek gönderiliyor; standCode boş gitmez.
      await updateStand(stand.standId, {
        standCode: codeChanged ? kod : undefined,
        salonId: salonChanged ? Number(form.salonId) : undefined,
        salonName: salonChanged ? salonName : undefined, // yalnızca mock kaydın adını kurmak için
        tabelaAdi: tabelaChanged ? form.tabelaAdi.trim() : undefined,
      }, { forceLive });
      // ⚠️ Salon değiştiyse stand YENİDEN ADLANDIRILDI ve yanıt yeni adı taşımıyor —
      // listeyi tazelemek (onSaved) burada zorunlu, kozmetik değil.
      setOkMsg(salonChanged
        ? t("standList.standBilgileriGuncellendiSalonDegistigi")
        : t("standList.standBilgileriGuncellendi"));
      onSaved?.();
    } catch (e) {
      setKodCakisti(isStandAdiCakismasi(e));
      setError(e?.response?.data?.message || e?.message || t("standList.standGuncellenemedi"));
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign(which) {
    const firma = which === "dekorator" ? dekoratorFirma : katilimciFirma;
    if (!firma) return;
    setAssigning(which);
    setError(null);
    setOkMsg(null);
    try {
      await assignFirmaToStand(stand.standId, {
        firmaId: firma.firmaId,
        firmaTypeId: which === "dekorator" ? FIRMA_TYPE.DEKORATOR : FIRMA_TYPE.KATILIMCI,
        fuarId,
      }, { forceLive });
      setOkMsg(t("standList.standaOlarakAtandi", { firmaName: firma.firmaName, v: which === "dekorator" ? t("standList.dekorator") : t("standList.katilimci") }));
      if (which === "dekorator") setDekoratorFirma(null); else setKatilimciFirma(null);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("standList.firmaAtanamadi"));
    } finally {
      setAssigning(null);
    }
  }

  const busy = saving || assigning !== null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={busy ? undefined : onClose} centered size="lg" scrollable>
        <ModalHeader toggle={busy ? undefined : onClose}>
          {t("standList.standDuzenle")}{" "}<span className="text-primary">{stand?.standName || `#${stand?.standId}`}</span>
        </ModalHeader>
        <ModalBody>
          {error && <Alert color="danger" className="py-2">{error}</Alert>}
          {okMsg && <Alert color="success" className="py-2">{okMsg}</Alert>}

          <StandIdentityFields
            form={form}
            update={update}
            salonOptions={salonOptionsWithCurrent}
            salonsUnavailable={salonsLoaded && salonOptionsWithCurrent.length === 0}
            disabled={busy}
            codeInvalid={kodCakisti}
          />
          <div className="d-flex justify-content-end mt-3">
            <Button color="primary" size="sm" onClick={handleSave} disabled={!dirty || busy}>
              {saving ? <Spinner size="sm" className="me-1" /> : <Pencil size={14} className="me-1" />}
              {t("standList.standBilgileriniKaydet")}
            </Button>
          </div>

          <hr />
          <div className="fw-semibold mb-2">
            {t("standList.firmaAtamasi")}
            <span className="text-muted fw-normal small ms-2">{t("standList.dogrudanAtamaAnindaYazilir")}</span>
          </div>
          <Row className="g-3">
            <Col md={6}>
              <div className="text-muted small mb-1">
                {t("standList.mevcutKatilimci")}{" "}<span className="fw-semibold text-body">{stand?.firmaName || "—"}</span>
              </div>
              <FirmaSearchSelect
                value={katilimciFirma}
                onChange={setKatilimciFirma}
                onCreateNew={() => setCreateFirmaFor("katilimci")}
                forceLive={forceLive}
                disabled={busy}
                placeholder={t("standList.katilimciFirmaAra")}
              />
              <Button
                color="success" outline size="sm" className="mt-2"
                disabled={!katilimciFirma || busy}
                onClick={() => handleAssign("katilimci")}
              >
                {assigning === "katilimci" ? <Spinner size="sm" className="me-1" /> : <UserPlus size={14} className="me-1" />}
                {t("standList.katilimciOlarakAta")}
              </Button>
            </Col>
            <Col md={6}>
              <div className="text-muted small mb-1">
                {t("standList.mevcutDekorator")}{" "}<span className="fw-semibold text-body">{stand?.dekoratorFirmaName || "—"}</span>
              </div>
              <FirmaSearchSelect
                value={dekoratorFirma}
                onChange={setDekoratorFirma}
                onCreateNew={() => setCreateFirmaFor("dekorator")}
                forceLive={forceLive}
                disabled={busy}
                placeholder={t("standList.dekoratorFirmaAra")}
              />
              <Button
                color="success" outline size="sm" className="mt-2"
                disabled={!dekoratorFirma || busy}
                onClick={() => handleAssign("dekorator")}
              >
                {assigning === "dekorator" ? <Spinner size="sm" className="me-1" /> : <UserPlus size={14} className="me-1" />}
                {t("standList.dekoratorOlarakAta")}
              </Button>
            </Col>
            <Col md={12}>
              <div className="text-muted small">
                <AlertTriangle size={12} className="me-1" />
                {t("standList.dogrudanAtamaMevcutFirmayiDegistirmez")}
              </div>
            </Col>
          </Row>

          <hr />
          <FirmaAtamaTalepPanel
            standId={stand?.standId}
            fuarId={fuarId}
            forceLive={forceLive}
            onChanged={onSaved}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={busy}>{t("common:kapat")}</Button>
        </ModalFooter>
      </Modal>

      <CreateFirmaModal
        key={createFirmaFor || "closed"}
        isOpen={createFirmaFor !== null}
        onClose={() => setCreateFirmaFor(null)}
        fuarId={fuarId}
        forceLive={forceLive}
        defaultRoleId={createFirmaFor === "dekorator" ? FIRMA_TYPE.DEKORATOR : FIRMA_TYPE.KATILIMCI}
        onCreated={(firma) => {
          if (createFirmaFor === "dekorator") setDekoratorFirma(firma);
          else setKatilimciFirma(firma);
          setCreateFirmaFor(null);
        }}
      />
    </>
  );
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export default function StandListPage() {
  const { t: tx } = useTranslation("projeler");
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const isLiveTestFuar = isLiveTestFuarId(selectedFuarId);

  const [viewMode, setViewMode] = useState("list");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // standId köprüsüyle açık revizyonu olan standlar (bkz. projectService.js bölüm 11)
  // — satır rozeti için, sayfalamadan bağımsız tek bir toplu sorgu.
  const [openRevisionStandIds, setOpenRevisionStandIds] = useState(new Set());
  // standId → bitmemiş revizyon sayısı — FileStatusIndicator'daki "Revizyon İstenen"
  // sayaç kutusu için, openRevisionStandIds ile aynı toplu sorgu mantığı.
  const [openRevisionCounts, setOpenRevisionCounts] = useState({});
  // standId → ANSWERED revizyon sayısı — "sıra bizde" rozeti. Kardeş sorgu, farklı
  // soru: diğeri "hâlâ üzerinde çalışılıyor mu", bu "beni mi bekliyor".
  const [pendingProposalCounts, setPendingProposalCounts] = useState({});
  // Düzenlenen stand satırı (null = kapalı). Satırın kendisini tutuyoruz ki modal
  // mevcut ad/salon/tabela/firma değerlerini ek bir istek atmadan doldurabilsin.
  const [editStand, setEditStand] = useState(null);
  // "Özel İzinler" düğmesinin rozeti — bekleyen talep sayısı (null = bilinmiyor/hata).
  const [ozelIzinCount, setOzelIzinCount] = useState(null);

  useEffect(() => { document.title = i18n.t("projeler:standList.standListesiFds"); }, []);

  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    getBekleyenOzelIzinler(selectedFuarId, { forceLive: isLiveTestFuar })
      .then((list) => { if (!ignore) setOzelIzinCount((list || []).length); })
      .catch(() => { if (!ignore) setOzelIzinCount(null); });
    return () => { ignore = true; };
  }, [selectedFuarId, isLiveTestFuar, reloadToken]);

  useEffect(() => {
    let ignore = false;
    // #283: bu üç toplu sorgu da canlı fuarlarda gerçek uçtan okunur (önceden USE_MOCK_DATA yüzünden hep sahte sayı geliyordu).
    const opts = { forceLive: isLiveTestFuar };
    Promise.all([getOpenRevisionStandIds(opts), getOpenRevisionCounts(opts), getPendingProposalCounts(opts)])
      .then(([ids, counts, pending]) => {
        if (!ignore) {
          setOpenRevisionStandIds(new Set(ids || []));
          setOpenRevisionCounts(counts || {});
          setPendingProposalCounts(pending || {});
        }
      })
      .catch(() => {
        if (!ignore) {
          setOpenRevisionStandIds(new Set());
          setOpenRevisionCounts({});
          setPendingProposalCounts({});
        }
      });
    return () => { ignore = true; };
  }, [isLiveTestFuar, reloadToken]);

  // Arama 300ms debounce edilir — değişince sayfa 1'e döner.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!infoMsg) return;
    const t = setTimeout(() => setInfoMsg(null), 3000);
    return () => clearTimeout(t);
  }, [infoMsg]);

  // KURAL: fetch mantığı Effect içinde tanımlanır, üst seviye fonksiyon olarak
  // paylaşılmaz — react-hooks/set-state-in-effect ihlalini önler.
  useEffect(() => {
    if (!selectedFuarId) return;
    let ignore = false;

    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await getStandOverviewList({
          fuarId: selectedFuarId, search, page, limit: pageSize, forceLive: isLiveTestFuar,
        });
        const fetchedRecords = data.records || [];
        if (!ignore) {
          // İLK BOYAMA roster'la yapılır: piller, İnşa/İş İzni ve taahhütnameler
          // artık burada — köprüyü beklemeye gerek yok (bkz. missing-pills-guide.md).
          setRecords(fetchedRecords);
          setTotal(data.total || 0);
          setLoading(false);
        }

        // İKİNCİ, ENGELLEMEYEN AŞAMA — sadece belge kutucuklarının okuduğu
        // fileApprovals/uploadedFiles için. Satır başına bir çağrı olduğu için
        // liste render'ını bloke ETMEZ; başarısız olursa kutucuklar "kayıt yok"
        // durumunda kalır, sayfanın geri kalanı etkilenmez.
        if (isLiveTestFuar && fetchedRecords.length > 0) {
          const bridged = await Promise.all(
            fetchedRecords.map((r) => getProjectByStandId(r.standId, { forceLive: true }).catch(() => null))
          );
          if (!ignore) {
            setRecords(fetchedRecords.map((r, i) => mergeLiveProjectIntoRow(r, bridged[i])));
          }
        }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [selectedFuarId, isLiveTestFuar, search, page, pageSize, reloadToken]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("standList.standListesi")} pageTitle={tx("standList.projeler")} />

        {error && <Alert color="danger">{error}</Alert>}
        {infoMsg && <Alert color="info">{infoMsg}</Alert>}

        {/* ─── Araç Çubuğu ─── */}
        <Card className="mb-3">
          <CardBody>
            <Row className="align-items-center g-2">
              <Col md="auto">
                <div className="btn-group" role="group">
                  <Button
                    color={viewMode === "list" ? "danger" : "success"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                  >
                    <ListIcon size={14} className="me-1" />{tx("standList.liste")}
                  </Button>
                  <Button
                    color={viewMode === "grid" ? "danger" : "success"}
                    size="sm"
                    onClick={() => setViewMode("grid")}
                  >
                    <LayoutGrid size={14} className="me-1" />{tx("standList.kart")}
                  </Button>
                </div>
              </Col>

              <Col md={4}>
                <InputGroup size="sm">
                  <span className="input-group-text"><Search size={14} /></span>
                  <Input
                    placeholder={tx("standList.standSalonFirmaVeyaDekorator")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    disabled={!selectedFuarId}
                  />
                </InputGroup>
              </Col>

              <Col md="auto">
                <InputGroup size="sm">
                  <span className="input-group-text">{tx("standList.gosterim")}</span>
                  <Input
                    type="select"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </Input>
                </InputGroup>
              </Col>

              <Col md="auto" className="ms-auto d-flex gap-2">
                <Button
                  color={filterOpen ? "danger" : "secondary"} outline={!filterOpen} size="sm"
                  onClick={() => setFilterOpen((o) => !o)}
                  title={tx("standList.gelismisArama")}
                >
                  <Filter size={14} />
                </Button>
                <Button color="secondary" outline size="sm" disabled title={tx("standList.siralamaYakinda")}>
                  <ArrowUpDown size={14} />
                </Button>
                <Button
                  color="secondary" outline size="sm"
                  onClick={() => setReloadToken((t) => t + 1)}
                  disabled={!selectedFuarId || loading}
                  title={tx("common:yenile")}
                >
                  <RefreshCw size={14} />
                </Button>
                <Button
                  color="warning" outline size="sm"
                  onClick={() => navigate("/Management/Projects/SpecialPermits")}
                  disabled={!selectedFuarId}
                >
                  <ShieldCheck size={14} className="me-1" />{tx("standList.ozelIzinler")}
                  {ozelIzinCount > 0 && <Badge color="danger" pill className="ms-1">{ozelIzinCount}</Badge>}
                </Button>
                {/* Popup değil, kendi sayfası: 2026-09-04'te "Stand Yarat" devraldı —
                    akışın iki kopyası olsaydı ad kuralı ve tabela adı önerisi zamanla
                    ayrışırdı. 2026-09-15'ten beri yolu /Management/Standlar/Yeni. */}
                <Button
                  color="success" size="sm"
                  onClick={() => navigate("/Management/Standlar/Yeni")}
                  disabled={!selectedFuarId}
                  title={isLiveTestFuar ? undefined : tx("standList.testFuarlari298299Disinda")}
                >
                  <Plus size={14} className="me-1" />{tx("standList.standYarat")}
                </Button>
              </Col>
            </Row>

            <Collapse isOpen={filterOpen}>
              <Row className="mt-3">
                <Col md="4">
                  <Input type="select" size="sm" disabled>
                    <option>{tx("standList.salonYakinda")}</option>
                  </Input>
                </Col>
                <Col md="4">
                  <Input type="select" size="sm" disabled>
                    <option>{tx("standList.dekoratorYakinda")}</option>
                  </Input>
                </Col>
                <Col md="4">
                  <Input type="select" size="sm" disabled>
                    <option>{tx("standList.durumYakinda")}</option>
                  </Input>
                </Col>
              </Row>
            </Collapse>
          </CardBody>
        </Card>

        {!selectedFuarId && (
          <Alert color="info">{tx("standList.listeyiGormekIcinOnceBir")}</Alert>
        )}

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && selectedFuarId && records.length === 0 && (
          <Alert color="secondary">{tx("standList.buFuaraAitStandBulunamadi")}</Alert>
        )}

        {/* ─── Liste Görünümü ─── */}
        {!loading && viewMode === "list" && records.map((s) => {
          const hasProject = Boolean(s.firmaId);
          // Canlı köprüde `hasProjectShell` yalnızca projectService.getProjectByStandId
          // sonucuna göre set edilir (bkz. mergeLiveProjectIntoRow) — katılımcı firma
          // atanmış olması Mongo proje kaydının var olduğu anlamına gelmez. Mock
          // kayıtlarda bu alan hiç yok, `!== false` her zaman true'ya düşer (davranış
          // değişmez).
          const hasShell = s.hasProjectShell !== false;
          const issue = hasIssue(s);
          const { birincil, ikincil, tabelaVar } = standBaslik(s);
          return (
            <Card key={s.id} className={`mb-3 border ${issue ? "border-danger" : "border-success"}`}>
              <CardBody>
                <Row className="align-items-center">
                  <Col md="auto">
                    {/* WAS (until #214): <StandThumbnail standNo={s.standNo ?? "—"} standCode={s.standCode ?? s.standName} /> */}
                    <StandThumbnail s={s} onOpen={() => navigate(`/Management/Projects/Stands/${s.id}`)} />
                  </Col>

                  <Col md={2} className="mb-2 mb-md-0">
                    {/* WAS (until #548): firma short name at fs-5 here and the stand's own name (#208) as a small line under the dekorator */}
                    {birincil ? (
                      <div className="fw-bold fs-5" title={tabelaVar ? tx("standList.standAdi") : (s.firmaName || undefined)}>
                        {birincil}
                        {hasProject && openRevisionStandIds.has(s.standId) && <OpenRevisionBadge />}
                        {hasProject && pendingProposalCounts[s.standId] > 0 && <PendingProposalBadge count={pendingProposalCounts[s.standId]} />}
                      </div>
                    ) : (
                      <div className="text-muted small">—</div>
                    )}
                    {ikincil && <div className="text-muted small text-truncate" title={s.firmaName || undefined}>{ikincil}</div>}
                    {hasProject && (
                      s.dekoratorFirmaName ? (
                        <div className="text-muted small">{s.dekoratorFirmaName}</div>
                      ) : (
                        <Badge color="danger" className="mt-1">{tx("standList.dekoratorTanimlanmamis")}</Badge>
                      )
                    )}
                    {s.serinProje && (
                      <Badge color="info" className="mt-1">
                        <Snowflake size={12} className="me-1" />{tx("standList.serinProje")}
                      </Badge>
                    )}
                  </Col>

                  <Col md={5}>
                    {hasProject && hasShell ? (
                      <>
                        {/* WAS (until #548): fw-medium, heavier than the stand's own name; now a muted small line */}
                        <div className="text-muted small mb-2">
                          {s.firmaName}
                        </div>
                        <div className="d-flex align-items-stretch flex-wrap gap-3">
                          <div className="d-flex align-items-center">
                            <StandBadgeRowFull s={s} />
                          </div>
                          <VDivider className="d-none d-md-block" />
                          <StandStatusBoxes s={s} isLive={isLiveTestFuar} />
                        </div>
                      </>
                    ) : hasProject ? (
                      <div className="text-muted d-flex align-items-center">
                        <Handshake size={16} className="me-2" />
                        {tx("standList.katilimciAtandiProjeKaydiHenuz")}
                      </div>
                    ) : (
                      <div className="text-muted d-flex align-items-center">
                        <Handshake size={16} className="me-2" />
                        {tx("standList.buStandIcinProjeKaydi")}
                      </div>
                    )}
                  </Col>

                  <Col md="auto" className="d-flex align-items-center gap-3 mt-2 mt-md-0 ms-auto">
                    <FileStatusIndicator s={s} openRevisionCount={openRevisionCounts[s.standId]} />
                    <Button
                      color="secondary" outline size="sm"
                      title={tx("standList.duzenleStandBilgileriFirmaAtamasi")}
                      onClick={() => setEditStand(s)}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      color="info" outline size="sm"
                      title={tx("common:goruntule")}
                      onClick={() => navigate(`/Management/Projects/Stands/${s.id}`)}
                    >
                      <Eye size={14} className="me-1" />{tx("standList.detaylar")}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          );
        })}

        {/* ─── Kart Görünümü ─── */}
        {!loading && viewMode === "grid" && (
          <Row>
            {records.map((s) => {
              const hasProject = Boolean(s.firmaId);
              const hasShell = s.hasProjectShell !== false;
              const issue = hasIssue(s);
              const { birincil, ikincil, tabelaVar } = standBaslik(s);
              return (
                <Col md={3} className="mb-4" key={s.id}>
                  <Card className={`h-100 border ${issue ? "border-danger" : "border-success"}`}>
                    <CardBody className="d-flex flex-column align-items-center text-center">
                      {/* WAS (until #214): <StandThumbnail standNo={s.standNo ?? "—"} standCode={s.standCode ?? s.standName} /> */}
                      <StandThumbnail s={s} onOpen={() => navigate(`/Management/Projects/Stands/${s.id}`)} />
                      {/* WAS (until #548): firmaName here and the stand's own name (#208) as a small line under it; the card keeps the long firma name when there is no stand name */}
                      <div className="fw-bold mt-2" title={tabelaVar ? tx("standList.standAdi") : undefined}>
                        {tabelaVar ? birincil : (s.firmaName || s.standName)}
                        {hasProject && openRevisionStandIds.has(s.standId) && <OpenRevisionBadge />}
                        {hasProject && pendingProposalCounts[s.standId] > 0 && <PendingProposalBadge count={pendingProposalCounts[s.standId]} />}
                      </div>
                      {ikincil && <div className="small text-muted" title={s.firmaName || undefined}>{ikincil}</div>}
                      <div className="text-muted small">
                        {hasProject ? s.salonName : `${s.standName} · ${s.salonName}`}
                      </div>

                      {hasProject && (
                        s.dekoratorFirmaName ? (
                          <div className="text-muted small mb-2">{s.dekoratorFirmaName}</div>
                        ) : (
                          <Badge color="danger" className="mt-1 mb-2">{tx("standList.dekoratorTanimlanmamis")}</Badge>
                        )
                      )}

                      {hasProject && hasShell ? (
                        <>
                          <div className="d-flex flex-wrap justify-content-center mt-1">
                            <StandBadgeRow s={s} />
                          </div>
                          <div className="mt-1">
                            <StandExtraInfoIndicator s={s} />
                          </div>
                        </>
                      ) : hasProject ? (
                        <div className="text-muted small mt-2">
                          <Handshake size={14} className="me-1" />{tx("standList.projeKaydiHenuzAcilmadi")}
                        </div>
                      ) : (
                        <div className="text-muted small mt-2">
                          <Handshake size={14} className="me-1" />{tx("standList.projeKaydiYok")}
                        </div>
                      )}

                      <div className="mt-3">
                        <StandActionsDropdown
                          onView={() => navigate(`/Management/Projects/Stands/${s.id}`)}
                          onEdit={() => setEditStand(s)}
                          onInfo={setInfoMsg}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {!loading && records.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="text-muted small">
              {tx("standList.toplam")}{" "}<strong>{total}</strong>{" "}{tx("standList.standSayfa", { page, totalPages })}
            </div>
            <div className="d-flex gap-2">
              <Button
                color="secondary" outline size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                color="secondary" outline size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* Reset-on-open: key satır kimliğinden geliyor, başka bir standa geçince
            form state'i sıfırlansın (CreateFirmaModal ile aynı desen). */}
        {editStand && (
          <EditStandModal
            key={`edit-${editStand.standId}`}
            isOpen
            stand={editStand}
            onClose={() => setEditStand(null)}
            onSaved={() => setReloadToken((t) => t + 1)}
            fuarId={selectedFuarId}
            forceLive={isLiveTestFuar}
          />
        )}
      </Container>
    </div>
  );
}
